import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ReportingFilterOptionsResponse } from '@interfaces/reports/reporting-filter-options.response';
import {
  ReportingGenerateRequest,
  ReportingGenerateResponse,
} from '@interfaces/reports/reporting-generate.response';
import { ReportExportRequest } from '@interfaces/reports/reports';
import {
  ReportingTemplateCreateRequest,
  ReportingTemplateDetail,
  ReportingTemplateSummary,
  ReportingTemplateUpdateRequest,
} from '@interfaces/reports/reporting-templates.response';

@Injectable({
  providedIn: 'root',
})
export class ReportingApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = this.normalizeBaseUrl(environment.reportingApiUrl);

  getFilterOptions() {
    const url = this.buildUrl('/api/reporting/filter-options');
    return this.http.get<ReportingFilterOptionsResponse>(url);
  }

  generateReports(payload: ReportingGenerateRequest) {
    const url = this.buildUrl('/api/reporting/generate');
    return this.http.post<ReportingGenerateResponse>(url, payload);
  }

  exportReportPdf(payload: ReportExportRequest) {
    const url = this.buildUrl('/api/reporting/export/pdf');
    return this.http.post(url, payload, {
      observe: 'response',
      responseType: 'blob' as const,
    });
  }

  exportReportCsv(payload: ReportExportRequest) {
    const url = this.buildUrl('/api/reporting/export/csv');
    return this.http.post(url, payload, {
      observe: 'response',
      responseType: 'blob' as const,
    });
  }

  getTemplates(username: string) {
    const url = this.buildUrl(
      `/api/reporting/templates?username=${encodeURIComponent(username)}`,
    );
    return this.http.get<ReportingTemplateSummary[]>(url);
  }

  getTemplateById(templateId: string) {
    const url = this.buildUrl(`/api/reporting/templates/${templateId}`);
    return this.http.get<ReportingTemplateDetail>(url);
  }

  createTemplate(payload: ReportingTemplateCreateRequest) {
    const url = this.buildUrl('/api/reporting/templates');
    return this.http.post<ReportingTemplateDetail>(url, payload);
  }

  updateTemplate(
    templateId: string,
    payload: ReportingTemplateUpdateRequest,
  ) {
    const url = this.buildUrl(`/api/reporting/templates/${templateId}`);
    return this.http.patch<ReportingTemplateDetail>(url, payload);
  }

  deleteTemplate(templateId: string) {
    const url = this.buildUrl(`/api/reporting/templates/${templateId}`);
    return this.http.delete<void>(url);
  }

  private buildUrl(path: string): string {
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return `${this.baseUrl}${path}`;
  }

  private normalizeBaseUrl(url: string): string {
    if (url.endsWith('/')) {
      return url.slice(0, -1);
    }
    return url;
  }
}
